import type { InventoryChoiceSetWithSelectionResponse } from '@yoop/server-model/inventory-category';
import { DeliveryMethod } from '@yoop/server-model/inventory-category';
import type { TokenChoice } from '@yoop/whitelabel-data';
import {
  FeeReason,
  FeeType,
  FeeValueType,
  TokenDeliveryMethod,
  type TokenFee,
} from '@yoop/whitelabel-data';
import {
  type BreakdownItemResponse,
  BreakdownType,
  Reason,
  ValueType,
} from '@yoop/server-model/fees';
import type { OfferSelectedInventoryChoiceResponse } from '@yoop/server-model/token-info';
import { isNilOrEmpty } from '@yoop/util';

export const deliveryMethodFrom = (method: DeliveryMethod) => {
  switch (method) {
    case DeliveryMethod.SHIPPING:
      return TokenDeliveryMethod.Shipping;
    case DeliveryMethod.PICKUP:
      return TokenDeliveryMethod.PickUp;
    default:
      return TokenDeliveryMethod.Digital;
  }
};

// TODO: [Token Choices Refactor] - Simplify these two functions when refactoring TokenChoice/ItemOption
export const tokenChoiceFrom = (
  choiceSet: InventoryChoiceSetWithSelectionResponse,
): TokenChoice => ({
  name: choiceSet.name,
  selection: choiceSet.selectedChoice.name,
  id: choiceSet.choiceSetId.toString(),
  priceDifference: 0,
});

export const tokenChoicesFrom = (
  selectedChoices: OfferSelectedInventoryChoiceResponse,
  choiceSets: InventoryChoiceSetWithSelectionResponse[],
): TokenChoice[] => {
  if (isNilOrEmpty(choiceSets)) {
    return [];
  }
  return (
    selectedChoices?.selectedChoiceSetList?.map((selectedChoice) => {
      const choiceSet = choiceSets.find((set) => set.choiceSetId === selectedChoice.choiceSetId);

      return {
        name: choiceSet.name,
        id: selectedChoice.choiceSetId.toString(),
        selection: choiceSet.choices.find((choice) => choice.id === selectedChoice.choiceId).name,
        priceDifference: 0,
      };
    }) ?? []
  );
};

export const tokenFeeFrom = (feeBreakdown: BreakdownItemResponse): TokenFee => {
  if (feeBreakdown.breakdownType === BreakdownType.DISCOUNT) {
    return {
      id: feeBreakdown.discountResponse?.discountId.toString(),
      type: FeeType.Discount,
      amount: feeBreakdown.amount,
      name: feeBreakdown.name,
    };
  }

  return {
    id: feeBreakdown.feeResponse?.feeId.toString(),
    type: FeeType.Regular,
    amount: feeBreakdown.amount,
    name: feeBreakdown.name,
    reason: feeReasonFrom(feeBreakdown.feeResponse?.reason),
    value: feeBreakdown.feeResponse?.value,
    valueType: feeValueTypeFrom(feeBreakdown.feeResponse?.valueType),
  };
};

const feeReasonFrom = (serverReason: Reason) => {
  switch (serverReason) {
    case Reason.YOOP:
      return FeeReason.Yoop;
    case Reason.PAYMENT:
      return FeeReason.Payment;
    case Reason.CUSTOM:
      return FeeReason.Custom;
    case Reason.TAX:
      return FeeReason.Tax;
    case Reason.PROFIT_SHARE:
      return FeeReason.ProfitShare;
    default:
      return undefined;
  }
};

const feeValueTypeFrom = (serverType: ValueType) => {
  switch (serverType) {
    case ValueType.AMOUNT:
      return FeeValueType.Amount;
    case ValueType.PERCENTAGE:
      return FeeValueType.Percent;
    case ValueType.PROFIT_PERCENTAGE:
      return FeeValueType.ProfitPercent;
    case ValueType.STEPPED:
      return FeeValueType.Stepped;
    case ValueType.TBD:
      return FeeValueType.Pending;
    default:
      return undefined;
  }
};
